import React, { useEffect } from 'react'
import { Alert, Col, Container, Form, Row } from 'react-bootstrap'
import { Controller, useForm, useWatch } from 'react-hook-form'

import { Button } from '../../../../../components/Button/Button'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { zoomSettingsDefaultValues, ZoomSettingsFormValues } from './models'
import { SelectDropdown, SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'

import { useZoomSettings } from './hooks'

export const ZoomSettings: React.FC = () => {
  const { zoomIntegration, isAutoJoinEnforced, isStrictJoinEnforced, resolvedZoomStrictJoinEnabled, meetingInProgress, isFetching, isUpdating, save } =
    useZoomSettings()

  const form = useForm<ZoomSettingsFormValues>({
    defaultValues: zoomSettingsDefaultValues as ZoomSettingsFormValues,
  })
  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty, dirtyFields },
  } = form

  const onSave = (data: ZoomSettingsFormValues) => save(data, dirtyFields)

  useEffect(() => {
    if (zoomIntegration) {
      const config = zoomIntegration.configuration

      const removeSessionValue = config.type === 'recording' ? (config.removeSessionWhenNoRecording ?? true) : true

      let autoJoinValue: boolean | undefined
      let strictJoinValue: boolean | undefined
      if (config.type === 'bot') {
        if (isAutoJoinEnforced) {
          autoJoinValue = config.enforcedAutoJoin
        } else {
          autoJoinValue = config.autoJoin
        }
        strictJoinValue = resolvedZoomStrictJoinEnabled
      } else {
        autoJoinValue = undefined
        strictJoinValue = undefined
      }

      reset({
        integrationType: config.type,
        autoJoin: autoJoinValue,
        strictJoinEnabled: strictJoinValue,
        removeSessionWhenNoRecording: removeSessionValue,
      })
    }
  }, [zoomIntegration, reset, isAutoJoinEnforced, resolvedZoomStrictJoinEnabled])

  const watchedType = useWatch({ control, name: 'integrationType' })

  if (isFetching) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  const zoomSettingsSelectOptions: SelectOption[] = [
    { id: 'bot', label: 'AI Assistant' },
    { id: 'recording', label: 'Zoom Recording' },
  ]

  return (
    <Container className='p-0'>
      <Row className='mb-4'>
        <Col>
          <h3>Zoom Settings</h3>
          <p className='text-muted'>Manage how Zocks interacts with your Zoom meetings.</p>
          <p>
            Zocks can capture your Zoom meetings in two ways: with <b>AI Assistant</b>, it joins the Zoom meeting as a participant to listen and transcribe, or
            with <b>Zoom Recording</b>, Zoom notifies Zocks when the meeting is available so the media can be briefly accessed to create a transcript and then
            deleted. Zocks does not store any meeting content - Zoom's recording is used only to generate the transcript
          </p>
        </Col>
      </Row>

      {meetingInProgress && (
        <Row className='mb-4'>
          <Col>
            <Alert variant='warning'>You cannot change your Zoom settings while you have an active meeting in progress on Zoom.</Alert>
          </Col>
        </Row>
      )}

      <Form onSubmit={handleSubmit(onSave)}>
        <Container className='d-flex flex-column gap-4 p-0'>
          <Row>
            <Col>
              <Controller
                name='integrationType'
                control={control}
                render={({ field, fieldState }) => {
                  const currentSelection = zoomSettingsSelectOptions.find((opt) => opt.id === field.value) || null

                  return (
                    <>
                      <Form.Group className='mb-3' controlId='integrationType'>
                        <Form.Label>Meeting Capture</Form.Label>
                        <SelectDropdown
                          options={zoomSettingsSelectOptions}
                          initialSelectedOption={currentSelection}
                          onOptionSelected={(option) => field.onChange(option.id)}
                          disabled={meetingInProgress}
                          placeholder='Select Integration Type'
                          className='w-100'
                        />
                      </Form.Group>
                      {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                    </>
                  )
                }}
              />
            </Col>
          </Row>

          {watchedType === 'bot' && (
            <>
              {isAutoJoinEnforced && (
                <Row className='zocks-mb-2 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
                  <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
                    <div className='d-flex align-self-center gap-3 align-items-center'>
                      <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                      <span className='zocks-text-gray-900'>Auto Join settings are managed at the enterprise level and cannot be modified here.</span>
                    </div>
                  </Col>
                </Row>
              )}
              <Row>
                <Col>
                  <Controller
                    name='autoJoin'
                    control={control}
                    render={({ field, fieldState }) => (
                      <>
                        <Form.Group className='mb-3' controlId='botName'>
                          <Form.Check
                            checked={field.value === true}
                            onChange={field.onChange}
                            type='switch'
                            id='autoJoin'
                            label={`Auto-join my ad-hoc Zoom meetings`}
                            disabled={meetingInProgress || isAutoJoinEnforced}
                          />
                        </Form.Group>
                        {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                      </>
                    )}
                  />
                </Col>
              </Row>
              <ScopedBlock missingScopeAction={MissingScopeAction.HIDE} quota={{ quota: 'integration.zoomStrictJoinEnabled.value' }}>
                {isStrictJoinEnforced && (
                  <Row className='zocks-mb-2 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
                    <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
                      <div className='d-flex align-self-center gap-3 align-items-center'>
                        <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                        <span className='zocks-text-gray-900'>Companion Mode settings are managed at the enterprise level and cannot be modified here.</span>
                      </div>
                    </Col>
                  </Row>
                )}
                <Row>
                  <Col>
                    <Controller
                      name='strictJoinEnabled'
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <Form.Group className='mb-3' controlId='strictJoinEnabled'>
                            <Form.Check
                              checked={field.value === true}
                              onChange={field.onChange}
                              type='switch'
                              id='strictJoinEnabled'
                              label='Join meetings in companion mode'
                              disabled={meetingInProgress || isStrictJoinEnforced}
                            />
                            <div className='paragraph-small zocks-text-gray-600'>
                              The AI Assistant is linked to your identity, joins with you and leaves when you do. When disabled, it joins independently of you.
                            </div>
                          </Form.Group>
                          {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                        </>
                      )}
                    />
                  </Col>
                </Row>
              </ScopedBlock>
            </>
          )}
          {watchedType === 'recording' && (
            <Row>
              <Col>
                <Controller
                  name='removeSessionWhenNoRecording'
                  control={control}
                  render={({ field, fieldState }) => (
                    <>
                      <Form.Group className='mb-3' controlId='removeSessionWhenNoRecording'>
                        <Form.Label>Recording</Form.Label>
                        <Form.Check
                          checked={field.value === true}
                          onChange={field.onChange}
                          type='switch'
                          id='removeSessionWhenNoRecordingCheckbox'
                          label={`Remove Meetings Without Recording`}
                          disabled={meetingInProgress}
                        />
                      </Form.Group>
                      {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                    </>
                  )}
                />
              </Col>
            </Row>
          )}
          <Row className='mt-2'>
            <Col>
              <Button variant='primary' loading={isUpdating} disabled={meetingInProgress || !isDirty} onClick={handleSubmit(onSave)}>
                Save Changes
              </Button>
            </Col>
          </Row>
        </Container>
      </Form>
    </Container>
  )
}
